import { useCallback, useEffect, useState } from 'react'
import { get, post } from '../lib/api'
import { t } from '../lib/i18n'
import type { User } from '../lib/auth'
import { roundName, roundsFor } from '../../shared/ranking'
import Icon from './Icon'

/**
 * The bracket, drawn whole.
 *
 * Every round is on the screen from the moment it is created, including the
 * rounds nobody has reached -- that is what people gather around, and a bracket
 * that grows a round at a time is a list of results rather than a bracket.
 *
 * A slot with both cars present is two tap targets. A slot still waiting shows
 * where its cars are coming from, so the shape reads before it is played.
 */

interface Tournament {
  id: string
  name: string
  size: number
  status: string
  winner_id: string | null
  winner_name: string | null
  created_at: number
}

interface Slot {
  id: string
  round: number
  position: number
  car_a: string | null
  car_b: string | null
  seed_a: number | null
  seed_b: number | null
  winner_id: string | null
  car_a_name: string | null
  car_b_name: string | null
}

const STATUS: Record<string, string> = {
  open: t('bracket.open'),
  complete: t('bracket.complete'),
  abandoned: t('bracket.abandoned'),
}

export default function Brackets({ user }: { user: User }) {
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  const [open, setOpen] = useState<{ tournament: Tournament; slots: Slot[] } | null>(null)
  const [size, setSize] = useState(8)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const mayAbandon = user.capabilities.includes('operations.write')

  const loadList = useCallback(async () => {
    try {
      setTournaments((await get<{ tournaments: Tournament[] }>('/api/tournaments')).tournaments)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }, [])

  useEffect(() => { void loadList() }, [loadList])

  async function create() {
    setBusy(true)
    setError(null)
    try {
      setOpen(await post<{ tournament: Tournament; slots: Slot[] }>('/api/tournaments', { size }))
      await loadList()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  async function openOne(id: string) {
    try {
      setOpen(await get<{ tournament: Tournament; slots: Slot[] }>(`/api/tournaments/${id}`))
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }

  async function decide(slot: Slot, winnerId: string) {
    if (!open) return
    setBusy(true)
    try {
      setOpen(await post(`/api/tournaments/${open.tournament.id}/slots/${slot.id}`, { winnerId }))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    } finally {
      setBusy(false)
    }
  }

  async function abandon() {
    if (!open) return
    try {
      setOpen(await post(`/api/tournaments/${open.tournament.id}/abandon`, {}))
      await loadList()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'))
    }
  }

  if (open) {
    const rounds = roundsFor(open.tournament.size)
    const decided = open.tournament.status !== 'open'
    return (
      <section className="card">
        <div className="row">
          <button type="button" className="link" onClick={() => setOpen(null)}>
            <Icon name="back" size={16} /> {t('bracket.back')}
          </button>
          <span className="spacer" />
          {mayAbandon && open.tournament.status === 'open' && (
            <button type="button" className="link" onClick={() => void abandon()}>{t('bracket.abandon')}</button>
          )}
        </div>

        <h1>{open.tournament.name}</h1>
        {open.tournament.winner_name && (
          <p className="ok" role="status">
            <Icon name="check" size={16} /> {t('bracket.winner')}: {open.tournament.winner_name}
          </p>
        )}
        {error && <p className="error" role="alert">{error}</p>}
        {!decided && <p className="muted small">{t('bracket.tapWinner')}</p>}

        {Array.from({ length: rounds }, (_, i) => i + 1).map((round) => (
          <div key={round} className="bracket-round">
            <h2>{roundName(round, rounds)}</h2>
            {open.slots.filter((s) => s.round === round).map((slot) => (
              <div key={slot.id} className="bracket-slot">
                {(['a', 'b'] as const).map((side) => {
                  const carId = side === 'a' ? slot.car_a : slot.car_b
                  const name = side === 'a' ? slot.car_a_name : slot.car_b_name
                  const seed = side === 'a' ? slot.seed_a : slot.seed_b
                  const won = !!slot.winner_id && slot.winner_id === carId
                  const lost = !!slot.winner_id && !won

                  if (!carId) return <div key={side} className="bracket-car is-waiting">{t('bracket.waiting')}</div>
                  return (
                    <button
                      key={side}
                      type="button"
                      className={`bracket-car${won ? ' has-won' : ''}${lost ? ' has-lost' : ''}`}
                      disabled={busy || decided || !!slot.winner_id || !slot.car_a || !slot.car_b}
                      onClick={() => void decide(slot, carId)}
                    >
                      {seed && <span className="bracket-seed">{seed}</span>}
                      {name}
                      {won && <Icon name="check" size={16} />}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        ))}
      </section>
    )
  }

  return (
    <section className="card">
      <h1>{t('bracket.title')}</h1>
      {error && <p className="error" role="alert">{error}</p>}

      <h2>{t('bracket.new')}</h2>
      <div className="row">
        <label className="inline">
          {t('bracket.size')}
          <select value={size} onChange={(e) => setSize(Number(e.target.value))}>
            <option value={4}>4</option>
            <option value={8}>8</option>
          </select>
        </label>
        <button type="button" className="primary" disabled={busy} onClick={() => void create()}>
          {t('bracket.create')}
        </button>
      </div>

      {tournaments.length === 0 ? (
        <p className="muted">{t('bracket.none')}</p>
      ) : (
        <ul className="list">
          {tournaments.map((tournament) => (
            <li key={tournament.id}>
              <button type="button" className="link" onClick={() => void openOne(tournament.id)}>
                {tournament.name}
              </button>
              <span className="muted small">
                {STATUS[tournament.status] ?? tournament.status}
                {tournament.winner_name && ` · ${tournament.winner_name}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
